import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback
} from "react";

import axios from "axios";
import io from "socket.io-client";

import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useLocalParticipant,
  useSpeakingParticipants,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";

import config from "../../../config";
import { toast } from "../../Common/Toast";

import { Camera } from "@mediapipe/camera_utils";
import { FaceMesh } from "@mediapipe/face_mesh";

import {
  FaWindowMaximize,
  FaWindowMinimize,
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash,
  FaTimes,
  FaUserCheck,
  FaUserClock,
  FaCheckCircle,
  FaSpinner,
  FaExpand,
  FaCompress,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
} from "react-icons/fa";

import { MdCallEnd } from "react-icons/md";
import { HiOutlineClipboardList } from "react-icons/hi";

import "./MilitarySessionVideoCall.css";
import MilitaryEndReviewModal from "./MilitaryEndReviewModal";

const getCurrentMinute = () => Math.floor(Date.now() / 1000 / 60) * 60;

// ==================== PARTICIPANT TILE ====================
const MilitaryParticipantTile = ({ track, isMainSpeaker, onMaximize, isMaximized }) => {
  const participantName = track.participant.name || track.participant.identity || "Unknown";
  const isLocal = track.participant.isLocal;

  return (
    <div className={`mil-participant-tile ${isMainSpeaker ? 'main-speaker' : ''} ${isMaximized ? 'maximized' : ''}`}>
      <div className="mil-participant-video">
        {track.publication?.kind === 'video' ? (
          <video
            ref={el => {
              if (el && track.publication?.track) {
                track.publication.track.attach(el);
              }
            }}
            autoPlay
            playsInline
            muted={isLocal}
            className="mil-video-element"
          />
        ) : (
          <div className="mil-no-video-placeholder">
            <FaUser />
          </div>
        )}
      </div>

      {!isLocal && (
        <button
          className="mil-maximize-btn"
          onClick={() => onMaximize(track)}
          title={isMaximized ? "Restore" : "Maximize"}
        >
          {isMaximized ? <FaCompress /> : <FaExpand />}
        </button>
      )}

      <div className="mil-participant-name-overlay">
        <span className="mil-participant-name">
          {isLocal ? 'You' : participantName}
        </span>
        {track.participant.isSpeaking && <span className="mil-speaking-indicator" />}
      </div>
    </div>
  );
};

// ==================== VIDEO LAYOUT ====================
const MilitaryVideoLayout = ({ onMaximize, maximizedTrack }) => {
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);
  const speakingParticipants = useSpeakingParticipants();
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const PARTICIPANTS_PER_PAGE = 9;
  const MAX_SIDEBAR_TILES = 3;

  useEffect(() => {
    if (!maximizedTrack && speakingParticipants.length > 0) {
      const speaker = tracks.find(t => t.participant.sid === speakingParticipants[0]?.sid);
      if (speaker) setActiveSpeaker(speaker);
    }
  }, [speakingParticipants, tracks, maximizedTrack]);

  const cameraTracks = tracks.filter(t => t.source === Track.Source.Camera);
  const screenShareTrack = tracks.find(t => t.source === Track.Source.ScreenShare);
  const mainTrack = maximizedTrack || activeSpeaker || cameraTracks[0];

  const sidebarTracks = cameraTracks
    .filter(t => t.participant.sid !== mainTrack?.participant.sid)
    .slice(0, MAX_SIDEBAR_TILES);

  const remainingCount = Math.max(0, cameraTracks.length - MAX_SIDEBAR_TILES - (mainTrack ? 1 : 0));
  const remainingTracks = cameraTracks.filter(t =>
    t.participant.sid !== mainTrack?.participant.sid &&
    !sidebarTracks.find(st => st.participant.sid === t.participant.sid)
  );
  const totalPages = Math.ceil(remainingTracks.length / PARTICIPANTS_PER_PAGE);
  const paginatedTracks = remainingTracks.slice(
    (currentPage - 1) * PARTICIPANTS_PER_PAGE,
    currentPage * PARTICIPANTS_PER_PAGE
  );

  const showSidebar = sidebarTracks.length > 0 || remainingCount > 0;

  return (
    <div className="mil-layout">
      <div className="mil-main-content">
        {screenShareTrack && (
          <div className="mil-screen-share">
            <MilitaryParticipantTile track={screenShareTrack} onMaximize={onMaximize} />
          </div>
        )}
        <div className="mil-main-speaker">
          {mainTrack ? (
            <MilitaryParticipantTile
              track={mainTrack}
              isMainSpeaker
              onMaximize={onMaximize}
              isMaximized={maximizedTrack?.participant.sid === mainTrack.participant.sid}
            />
          ) : (
            <div className="mil-empty">
              <div className="mil-empty-icon">🎥</div>
              <p>Waiting for participants...</p>
            </div>
          )}
        </div>
      </div>

      {showSidebar && (
        <div className="mil-sidebar">
          {sidebarTracks.map(t => (
            <div key={t.participant.sid} className="mil-sidebar-tile" onClick={() => onMaximize(t)}>
              <MilitaryParticipantTile track={t} onMaximize={onMaximize} isMaximized={false} />
            </div>
          ))}
          {remainingCount > 0 && (
            <button className="mil-show-more" onClick={() => setShowParticipantsPanel(true)}>
              <FaUsers /> <span>+{remainingCount}</span>
            </button>
          )}
        </div>
      )}

      {showParticipantsPanel && (
        <div className="mil-participants-panel">
          <div className="mil-panel-header">
            <h3>All Participants ({cameraTracks.length})</h3>
            <button onClick={() => setShowParticipantsPanel(false)}><FaTimes /></button>
          </div>
          <div className="mil-panel-grid">
            {paginatedTracks.map(t => (
              <div
                key={t.participant.sid}
                className="mil-panel-item"
                onClick={() => { onMaximize(t); setShowParticipantsPanel(false); }}
              >
                <MilitaryParticipantTile track={t} onMaximize={onMaximize} isMaximized={false} />
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mil-pagination">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <FaChevronLeft />
              </button>
              <span>{currentPage}/{totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== CONTROLS ====================
const MilitaryControls = ({ isAdmin, onToggleMinimize, handleEndMeeting, isEnding, setShowAdminPanel, showAdminPanel }) => {
  const localParticipant = useLocalParticipant();
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [init, setInit] = useState(false);

  useEffect(() => {
    if (!localParticipant.localParticipant || init) return;
    const p = localParticipant.localParticipant;
    setIsMuted(p.getTrackPublication('microphone')?.isMuted || false);
    setIsVideoOff(p.getTrackPublication('camera')?.isMuted || false);
    setInit(true);
  }, [localParticipant.localParticipant, init]);

  useEffect(() => {
    if (!localParticipant.localParticipant) return;
    const p = localParticipant.localParticipant;
    const onMute = (pub) => { if (pub.kind === 'audio') setIsMuted(true); else if (pub.kind === 'video') setIsVideoOff(true); };
    const onUnmute = (pub) => { if (pub.kind === 'audio') setIsMuted(false); else if (pub.kind === 'video') setIsVideoOff(false); };
    p.on('trackMuted', onMute);
    p.on('trackUnmuted', onUnmute);
    return () => { p.off('trackMuted', onMute); p.off('trackUnmuted', onUnmute); };
  }, [localParticipant.localParticipant]);

  const toggleMute = async () => {
    if (localParticipant.localParticipant) {
      try {
        await localParticipant.localParticipant.setMicrophoneEnabled(!!isMuted);
      } catch (e) {
        console.error('Failed to toggle mic:', e);
      }
    }
  };

  const toggleVideo = async () => {
    if (localParticipant.localParticipant) {
      try {
        await localParticipant.localParticipant.setCameraEnabled(!!isVideoOff);
      } catch (e) {
        console.error('Failed to toggle camera:', e);
      }
    }
  };

  return (
    <div className="mil-controls">
      <div className="mil-controls-left">
        {isAdmin && (
          <button
            className={`mil-ctrl-btn ${showAdminPanel ? 'active' : ''}`}
            onClick={() => setShowAdminPanel(prev => !prev)}
          >
            <HiOutlineClipboardList /> <span>Soldiers</span>
          </button>
        )}
      </div>
      <div className="mil-controls-center">
        <button className={`mil-ctrl-btn ${isMuted ? 'active' : ''}`} onClick={toggleMute}>
          {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
          <span>{isMuted ? 'Unmute' : 'Mute'}</span>
        </button>
        <button className={`mil-ctrl-btn ${isVideoOff ? 'active' : ''}`} onClick={toggleVideo}>
          {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
          <span>{isVideoOff ? 'Start' : 'Stop'}</span>
        </button>
        <button className="mil-ctrl-btn mil-end-btn" onClick={handleEndMeeting} disabled={isEnding}>
          <MdCallEnd /> <span>{isEnding ? 'Ending...' : 'End'}</span>
        </button>
      </div>
      <div className="mil-controls-right">
        {onToggleMinimize && (
          <button className="mil-ctrl-btn" onClick={onToggleMinimize} title="Minimize">
            <FaWindowMinimize />
          </button>
        )}
      </div>
    </div>
  );
};

// ==================== ADMIN PROGRESS POPUP ====================
const MilitaryAdminProgress = ({ show, onClose, remoteParticipants, soldierProgress, totalQuestions }) => {
  if (!show) return null;
  const total = remoteParticipants.length;

  // FIX: use String(soldierId) for consistent key lookup
  const completed = remoteParticipants.filter(p => {
    const key = String(p.soldierId || p.userId);
    return soldierProgress[key]?.completed;
  }).length;

  return (
    <div className="mil-popup-overlay" onClick={onClose}>
      <div className="mil-popup" onClick={e => e.stopPropagation()}>
        <div className="mil-popup-header">
          <div className="mil-popup-title"><FaUserCheck /> <h3>Soldier Progress</h3></div>
          <button onClick={onClose}><FaTimes /></button>
        </div>
        <div className="mil-popup-stats">
          <div className="mil-stat"><FaUserClock /><div><span>{total}</span><span>Total</span></div></div>
          <div className="mil-stat"><FaCheckCircle /><div><span>{completed}</span><span>Completed</span></div></div>
          <div className="mil-stat"><FaSpinner /><div><span>{total - completed}</span><span>In Progress</span></div></div>
        </div>
        <div className="mil-popup-list">
          {remoteParticipants.length === 0 ? (
            <div className="mil-empty-list"><p>No soldiers joined yet</p></div>
          ) : (
            remoteParticipants.map(p => {
              // FIX: always use String() for consistent map key
              const key = String(p.soldierId || p.userId);
              const prog = soldierProgress[key] || {};
              const answered = prog.answeredCount || 0;
              const totalQ = prog.totalQuestions || totalQuestions || 30;
              const pct = totalQ > 0 ? Math.round((answered / totalQ) * 100) : 0;
              return (
                <div key={p.socketId || p.userId} className={`mil-candidate-card ${prog.completed ? 'completed' : ''}`}>
                  <div className="mil-candidate-header">
                    {/* <div className="mil-avatar">{p.displayName || `Soldier ${p.userId}`}</div> */}
                    <div className="mil-candidate-info">
                      <span className="mil-name">{p.displayName || `Soldier ${p.userId}`}</span>
                      <span className={`mil-status ${prog.completed ? 'completed' : 'active'}`}>
                        {prog.completed ? 'Completed' : `${answered}/${totalQ} answered`}
                      </span>
                    </div>
                    <div className="mil-pct">{pct}%</div>
                  </div>
                  <div className="mil-progress-bar">
                    <div className="mil-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== QUESTION PANEL ====================
const MilitaryQuestionPanel = ({ show, question, index, total, onSubmit, onSkip, isSubmitting }) => {
  if (!show || !question) return null;

  return (
    <div className="mil-question-panel">
      <div className="mil-q-header">
        <div className="mil-q-progress">Question {index} of {total}</div>
        {/* <div className="mil-q-timer">⏱️ Take your time to answer</div> */}
      </div>
      <div className="mil-q-text">{question.text}</div>
      <div className="mil-q-actions">
        <button className="mil-skip" onClick={onSkip} disabled={isSubmitting}>Skip</button>
        <button className="mil-submit" onClick={onSubmit} disabled={isSubmitting}>Submit Answer</button>
      </div>
    </div>
  );
};

// ==================== WAITING PANEL ====================
const MilitaryWaitingPanel = ({ timeRemaining, answeredCount, totalQuestions }) => (
  <div className="mil-waiting">
    <h3>Next question in {timeRemaining}s</h3>
    <div className="mil-waiting-progress">
      <div className="mil-progress-text">Question {answeredCount + 1} of {totalQuestions}</div>
      <div className="mil-progress-bar">
        <div
          className="mil-progress-fill"
          style={{ width: `${((answeredCount) / totalQuestions) * 100}%` }}
        />
      </div>
    </div>
  </div>
);

// ==================== COMPLETION PANEL ====================
const MilitaryCompletionPanel = ({ onClose }) => (
  <div className="mil-completion">
    <div className="mil-completion-icon">🎉</div>
    <h3>Session Complete!</h3>
    <p>You have answered all questions for this week.</p>
    <button onClick={onClose}>Close</button>
  </div>
);

// ==================== MAIN COMPONENT ====================
const MilitarySessionVideoCall = forwardRef(({
  sessionId,
  individualSessionId,
  programId,
  weekNumber,
  userId,
  token,
  user,
  selectedUser,
  isAdmin = false,
  teamName = "",
  teamId = null,
  onSessionComplete,
  onClose,
  isMinimized = false,
  onToggleMinimize,
  onAbandonSession,
  isBatchSession = false,
  batchId = null,
  soldierId = null,
  groupCandidates = [],
  batchData = null,
}, ref) => {

  // ===== STATE =====
  const [livekitToken, setLivekitToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [connected, setConnected] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [remoteParticipants, setRemoteParticipants] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [soldierProgress, setSoldierProgress] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [showQuestionPanel, setShowQuestionPanel] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(30);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const [nextQuestionTime, setNextQuestionTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [maximizedTrack, setMaximizedTrack] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [showMilitaryReview, setShowMilitaryReview] = useState(false);
  const [reviewSessionId, setReviewSessionId] = useState(null);
  const [reviewBatchId, setReviewBatchId] = useState(null);
  const [reviewTeamId, setReviewTeamId] = useState(null);
  const [currentBatchData, setCurrentBatchData] = useState(batchData || null);
  const [activeMilitarySession] = useState({
    week_number: weekNumber || 1,
    program_id: programId,
  });

  const [allQuestions, setAllQuestions] = useState([]);

  // ===== CONTROL REFS =====
  const socketRef = useRef(null);
  const questionTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const isFetchingRef = useRef(false);
  const isEndingRef = useRef(false);
  const hasEndedRef = useRef(false);
  const livekitRoomRef = useRef(null);
  const analyticsSavedRef = useRef(false);
  const collectionIntervalRef = useRef(null);

  // ===== ANALYTICS VIDEO/STREAM REFS =====
  const analyticsVideoRef = useRef(null);
  const analyticsStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);

  // ===== FACE MESH REFS =====
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const faceMeshInitializedRef = useRef(false);
  const faceDetectedRef = useRef(false);

  // ===== AUDIO REFS =====
  const audioContextRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioAnalysisFrameRef = useRef(null);
  const audioInitializedRef = useRef(false);
  const audioDetectedRef = useRef(false);

  // ===== TRACKING REFS =====
  const lastBlinkTimeRef = useRef(null);
  const blinkCountRef = useRef(0);
  const lastFrameTimeRef = useRef(Date.now());
  const lastSpeechTimeRef = useRef(Date.now());
  const speechCountRef = useRef(0);
  const pauseStartTimeRef = useRef(null);
  const pitchHistoryRef = useRef([]);
  const lastInterruptTimeRef = useRef(null);
  const lastCameraMinRef = useRef(null);
  const lastVoiceMinRef = useRef(null);

  // ===== METRICS HISTORY REFS =====
  const camHist = useRef({
    eye_contact_duration: [], blink_rate: [], facial_tension: [], smile_authenticity: [],
    head_tilt_openness: [], lean_posture: [], micro_joy: [], micro_fear: [], micro_disgust: [], micro_surprise: [],
  });
  const voiceHist = useRef({
    pitch_variability: [], speech_rate: [], pauses: [], vocal_tremor: [], warmth_resonance: [], interrupt_patterns: [],
  });

  const camCurrent = useRef({
    eye_contact_duration: 0.5, blink_rate: 0, facial_tension: 0.5, smile_authenticity: 0.5,
    head_tilt_openness: 0.5, lean_posture: 0.5, micro_joy: 0.5, micro_fear: 0.1, micro_disgust: 0.05, micro_surprise: 0.2,
  });
  const voiceCurrent = useRef({
    pitch_variability: 0.5, speech_rate: 0, pauses: 0, vocal_tremor: 0.1, warmth_resonance: 0.5, interrupt_patterns: 0,
  });

  // FIX: always use sessionId as the room identifier — never batchId
  const livekitRoomName = `military_${sessionId}`;
  const dbSessionId = individualSessionId || sessionId;

  // ===== SYNC REFS =====
  useEffect(() => { hasEndedRef.current = hasEnded; }, [hasEnded]);
  useEffect(() => { if (batchData) setCurrentBatchData(batchData); }, [batchData]);

  const handleMaximize = useCallback((track) => {
    setMaximizedTrack(prev =>
      prev?.participant.sid === track.participant.sid ? null : track
    );
  }, []);

  // ==================== ANALYTICS HELPERS ====================
  const calcAvg = useCallback((arr) => {
    if (!arr?.length) return 0;
    return arr.reduce((sum, item) => sum + item.value, 0) / arr.length;
  }, []);

  const addCameraDataPoint = useCallback(() => {
    if (isAdmin || !faceDetectedRef.current) return;
    const ts = getCurrentMinute();
    if (lastCameraMinRef.current === ts) return;
    lastCameraMinRef.current = ts;
    Object.keys(camHist.current).forEach(k => {
      let v = camCurrent.current[k];
      if (typeof v !== 'number') v = 0.5;
      if (k === 'blink_rate') v = Math.min(30, Math.max(0, v));
      camHist.current[k].push({ ts, value: v });
    });
  }, [isAdmin]);

  const addVoiceDataPoint = useCallback(() => {
    if (isAdmin || !audioDetectedRef.current) return;
    const ts = getCurrentMinute();
    if (lastVoiceMinRef.current === ts) return;
    lastVoiceMinRef.current = ts;
    Object.keys(voiceHist.current).forEach(k => {
      let v = voiceCurrent.current[k];
      if (typeof v !== 'number') v = 0;
      voiceHist.current[k].push({ ts, value: v });
    });
  }, [isAdmin]);

  // ==================== SAVE ANALYTICS ====================
  const saveAnalytics = useCallback(async () => {
    if (isAdmin || analyticsSavedRef.current) return;

    addCameraDataPoint();
    addVoiceDataPoint();

    if (
      camHist.current.eye_contact_duration.length === 0 &&
      voiceHist.current.pitch_variability.length === 0
    ) {
      console.log('No analytics data to save');
      analyticsSavedRef.current = true;
      return;
    }

    const payload = {
      soldierId,
      sessionId: dbSessionId,
      cameraMetrics: camHist.current,
      voiceMetrics: voiceHist.current,
      summary: {
        avgEyeContact: calcAvg(camHist.current.eye_contact_duration),
        avgBlinkRate: calcAvg(camHist.current.blink_rate),
        avgFacialTension: calcAvg(camHist.current.facial_tension),
        avgSmileAuthenticity: calcAvg(camHist.current.smile_authenticity),
        avgHeadTiltOpenness: calcAvg(camHist.current.head_tilt_openness),
        avgPitchVariability: calcAvg(voiceHist.current.pitch_variability),
        avgSpeechRate: calcAvg(voiceHist.current.speech_rate),
        avgPauses: calcAvg(voiceHist.current.pauses),
        avgVocalTremor: calcAvg(voiceHist.current.vocal_tremor),
        avgWarmthResonance: calcAvg(voiceHist.current.warmth_resonance),
        totalInterruptions: voiceHist.current.interrupt_patterns.filter(i => i.value === 1).length,
      },
    };

    try {
      const response = await axios.post(
        `${config.baseURL}/api/military/sessions/${dbSessionId}/analytics/save`,
        payload,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
      );
      if (response.data.success) {
        analyticsSavedRef.current = true;
        console.log('✅ Analytics saved for soldier:', soldierId);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Analytics save error:', error.message);
      return false;
    }
  }, [isAdmin, soldierId, dbSessionId, token, addCameraDataPoint, addVoiceDataPoint, calcAvg]);

  // ==================== FACE MESH ====================
  const processFaceAnalytics = useCallback((landmarks) => {
    if (isAdmin) return;
    const now = Date.now();
    if (!faceDetectedRef.current) faceDetectedRef.current = true;

    const lm = landmarks;

    const li = lm[133], ri = lm[362];
    if (li && ri) camCurrent.current.eye_contact_duration =
      Math.min(0.95, Math.max(0.3, 0.7 + Math.abs(li.x - ri.x) * 0.3));

    const elapsed = (now - lastFrameTimeRef.current) / 1000;
    if (elapsed >= 1) {
      camCurrent.current.blink_rate = Math.min(25, Math.max(8, (blinkCountRef.current / elapsed) * 60));
      blinkCountRef.current = 0;
      lastFrameTimeRef.current = now;
    }

    const bl = lm[70], br = lm[300];
    if (bl && br) camCurrent.current.facial_tension =
      Math.min(0.9, Math.max(0.1, Math.abs(bl.y - br.y) * 10));

    const ml = lm[61], mr = lm[291], mt = lm[13], mb = lm[14];
    if (ml && mr && mt && mb) {
      const w = Math.abs(ml.x - mr.x), h = Math.abs(mt.y - mb.y);
      camCurrent.current.smile_authenticity = Math.min(0.95, Math.max(0.3, (h / (w + 0.01)) * 3));
    }

    const nose = lm[1], le = lm[33], re = lm[263];
    if (nose && le && re) {
      const ey = (le.y + re.y) / 2;
      camCurrent.current.head_tilt_openness = Math.min(0.9, Math.max(0.3, 0.6 + (nose.y - ey) * 2));
    }

    const ls = lm[11], rs = lm[12];
    if (ls && rs) {
      const sw = Math.abs(ls.x - rs.x);
      camCurrent.current.lean_posture = Math.max(0.4, Math.min(0.9, 1 - Math.min(1, sw) + 0.4));
    }

    if (ml && mr) camCurrent.current.micro_joy = Math.min(0.95, Math.max(0.3, Math.abs(ml.x - mr.x) * 2));
    if (bl && br) camCurrent.current.micro_fear = Math.min(0.9, Math.max(0.05, Math.abs(bl.y - br.y) * 5));

    const lt = lm[159], lb = lm[145], rt2 = lm[386], rb2 = lm[374];
    if (lt && lb && rt2 && rb2) {
      const lo = Math.abs(lt.y - lb.y), ro = Math.abs(rt2.y - rb2.y);
      if ((lo < 0.01 || ro < 0.01) && (now - (lastBlinkTimeRef.current || 0)) > 200) {
        blinkCountRef.current++;
        lastBlinkTimeRef.current = now;
      }
    }
  }, [isAdmin]);

  const stopFaceMesh = useCallback(async () => {
    if (cameraRef.current) { try { await cameraRef.current.stop(); } catch (_) { } cameraRef.current = null; }
    if (faceMeshRef.current) { try { await faceMeshRef.current.close(); } catch (_) { } faceMeshRef.current = null; }
    faceMeshInitializedRef.current = false;
    faceDetectedRef.current = false;
  }, []);

  const initFaceMesh = useCallback(async (videoOff = false) => {
    if (
      isAdmin ||
      videoOff ||
      faceMeshInitializedRef.current ||
      !localVideoRef.current ||
      !localStreamRef.current
    ) return;

    try {
      await stopFaceMesh();

      faceMeshRef.current = new FaceMesh({
        locateFile: f => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${f}`,
      });
      faceMeshRef.current.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      faceMeshRef.current.onResults(results => {
        if (isAdmin) return;
        if (results.multiFaceLandmarks?.length > 0 && !isMinimized) {
          processFaceAnalytics(results.multiFaceLandmarks[0]);
        } else {
          faceDetectedRef.current = false;
        }
      });

      cameraRef.current = new Camera(localVideoRef.current, {
        onFrame: async () => {
          if (isAdmin || !faceMeshRef.current || !localVideoRef.current || isMinimized) return;
          try {
            await faceMeshRef.current.send({ image: localVideoRef.current });
          } catch (_) { }
        },
        width: 640,
        height: 480,
      });

      await cameraRef.current.start();
      faceMeshInitializedRef.current = true;
      console.log('✅ FaceMesh initialized');
    } catch (err) {
      console.warn('FaceMesh init error:', err.message);
    }
  }, [isAdmin, isMinimized, processFaceAnalytics, stopFaceMesh]);

  // ==================== AUDIO ANALYTICS ====================
  const startAudioAnalysis = useCallback(() => {
    if (!audioAnalyserRef.current || audioContextRef.current?.state !== 'running') return;

    const freqArr = new Uint8Array(audioAnalyserRef.current.frequencyBinCount);
    const timeArr = new Uint8Array(audioAnalyserRef.current.fftSize);
    let lastVoiceActivity = false;

    const analyze = () => {
      if (!audioAnalyserRef.current) return;
      try {
        audioAnalyserRef.current.getByteFrequencyData(freqArr);
        audioAnalyserRef.current.getByteTimeDomainData(timeArr);

        const energy = freqArr.reduce((s, v) => s + v, 0) / freqArr.length / 255;
        const speaking = energy > 0.1;
        if (!audioDetectedRef.current && energy > 0.05) audioDetectedRef.current = true;

        if (speaking !== lastVoiceActivity) {
          if (speaking) {
            const now = Date.now();
            voiceCurrent.current.interrupt_patterns =
              lastInterruptTimeRef.current && (now - lastInterruptTimeRef.current) < 500 ? 1 : 0;
            lastInterruptTimeRef.current = now;
          }
          lastVoiceActivity = speaking;
        }

        let maxCorr = 0, pitch = 120;
        for (let lag = 20; lag < Math.min(1000, timeArr.length - 100); lag++) {
          let corr = 0;
          for (let i = 0; i < timeArr.length - lag; i++) {
            corr += (timeArr[i] - 128) * (timeArr[i + lag] - 128);
          }
          corr /= (timeArr.length - lag);
          if (corr > maxCorr && corr > 0) {
            const p = audioContextRef.current.sampleRate / lag;
            if (p > 80 && p < 300) { maxCorr = corr; pitch = p; }
          }
        }
        pitch = Math.min(250, Math.max(85, pitch));
        pitchHistoryRef.current.push(pitch);
        if (pitchHistoryRef.current.length > 100) pitchHistoryRef.current.shift();

        if (pitchHistoryRef.current.length >= 2) {
          const recent = pitchHistoryRef.current.slice(-10);
          const mean = recent.reduce((a, b) => a + b, 0) / recent.length;
          const variance = recent.reduce((a, b) => a + (b - mean) ** 2, 0) / recent.length;
          voiceCurrent.current.pitch_variability = Math.min(0.9, Math.sqrt(variance) / 50);
        }

        const now = Date.now();
        if (speaking) speechCountRef.current++;
        const speechElapsed = (now - lastSpeechTimeRef.current) / 1000;
        if (speechElapsed >= 1) {
          voiceCurrent.current.speech_rate = Math.min(180, Math.max(80, (speechCountRef.current / speechElapsed) * 60));
          speechCountRef.current = 0;
          lastSpeechTimeRef.current = now;
        }

        if (speaking) { pauseStartTimeRef.current = null; }
        else if (!pauseStartTimeRef.current) pauseStartTimeRef.current = now;
        voiceCurrent.current.pauses = pauseStartTimeRef.current
          ? Math.min(0.4, (now - pauseStartTimeRef.current) / 1000 / 15)
          : 0.15;

        if (pitchHistoryRef.current.length >= 10) {
          const r = pitchHistoryRef.current.slice(-10);
          const m = r.reduce((a, b) => a + b, 0) / r.length;
          voiceCurrent.current.vocal_tremor = Math.min(0.3, r.reduce((a, b) => a + Math.abs(b - m), 0) / r.length / 80);
        }

        const low = freqArr.slice(0, 100).reduce((a, b) => a + b, 0);
        const total = freqArr.reduce((a, b) => a + b, 0);
        voiceCurrent.current.warmth_resonance = Math.min(0.85, Math.max(0.4, low / (total || 1)));
      } catch (_) { }

      audioAnalysisFrameRef.current = requestAnimationFrame(analyze);
    };
    analyze();
  }, []);

  const stopAudioAnalytics = useCallback(() => {
    if (audioAnalysisFrameRef.current) { cancelAnimationFrame(audioAnalysisFrameRef.current); audioAnalysisFrameRef.current = null; }
    try { audioSourceRef.current?.disconnect(); } catch (_) { }
    audioSourceRef.current = null;
    audioAnalyserRef.current = null;
    try { audioContextRef.current?.close(); } catch (_) { }
    audioContextRef.current = null;
    audioInitializedRef.current = false;
    audioDetectedRef.current = false;
  }, []);

  const stopAnalytics = useCallback(() => {
    stopFaceMesh();
    stopAudioAnalytics();
  }, [stopFaceMesh, stopAudioAnalytics]);

  // ==================== START ANALYTICS STREAM ====================
  const startAnalyticsStream = useCallback(async () => {
    if (isAdmin) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });

      analyticsStreamRef.current = stream;

      if (analyticsVideoRef.current) {
        analyticsVideoRef.current.srcObject = stream;

        await new Promise((resolve) => {
          if (analyticsVideoRef.current.readyState >= 2) {
            resolve();
          } else {
            analyticsVideoRef.current.onloadeddata = resolve;
          }
        });

        localVideoRef.current = analyticsVideoRef.current;
        localStreamRef.current = stream;

        await initFaceMesh();
      }

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      audioAnalyserRef.current = audioContextRef.current.createAnalyser();
      audioAnalyserRef.current.fftSize = 2048;
      audioSourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      audioSourceRef.current.connect(audioAnalyserRef.current);

      const resume = async () => {
        if (audioContextRef.current?.state === 'suspended') {
          await audioContextRef.current.resume();
          audioInitializedRef.current = true;
          startAudioAnalysis();
          document.removeEventListener('click', resume);
          document.removeEventListener('touchstart', resume);
        }
      };
      document.addEventListener('click', resume);
      document.addEventListener('touchstart', resume);

      if (audioContextRef.current.state === 'running') {
        audioInitializedRef.current = true;
        startAudioAnalysis();
      }

      console.log('✅ Analytics stream started');
    } catch (error) {
      console.error('Failed to start analytics stream:', error);
    }
  }, [isAdmin, initFaceMesh, startAudioAnalysis]);

  // ==================== CLEANUP ====================
  const cleanupMedia = useCallback(async () => {
    hasEndedRef.current = true;

    if (questionTimerRef.current) { clearTimeout(questionTimerRef.current); questionTimerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    if (collectionIntervalRef.current) { clearInterval(collectionIntervalRef.current); collectionIntervalRef.current = null; }

    stopAnalytics();

    if (analyticsStreamRef.current) {
      analyticsStreamRef.current.getTracks().forEach(track => track.stop());
      analyticsStreamRef.current = null;
    }

    if (!isAdmin && livekitRoomRef.current) {
      try {
        const localParticipant = livekitRoomRef.current.localParticipant;
        if (localParticipant) {
          await localParticipant.setCameraEnabled(false);
          await localParticipant.setMicrophoneEnabled(false);
          localParticipant.getTrackPublications().forEach(pub => { if (pub?.track) pub.track.stop(); });
        }
        if (typeof livekitRoomRef.current.disconnect === 'function') {
          await livekitRoomRef.current.disconnect();
        }
      } catch (error) {
        console.error('Error cleaning up LiveKit tracks:', error);
      }
    }

    setShowQuestionPanel(false);
    setWaitingForNext(false);
    setCurrentQuestion(null);
    setSessionCompleted(true);
    setHasEnded(true);
    setConnected(false);

    if (socketRef.current) {
      try {
        socketRef.current.emit("leave-military-room", { sessionId, soldierId });
        await new Promise(resolve => setTimeout(resolve, 300));
        socketRef.current.disconnect();
        socketRef.current = null;
      } catch (e) {
        console.error('Error disconnecting socket:', e);
      }
    }
  }, [isAdmin, stopAnalytics, sessionId, soldierId]);

  // ==================== END MEETING ====================

 const handleEndMeeting = useCallback(async () => {
  if (isEndingRef.current || hasEndedRef.current) return;

  isEndingRef.current = true;
  setIsEnding(true);

  try {
    if (!isAdmin && !analyticsSavedRef.current) {
      try {
        await saveAnalytics();
      } catch (err) {
        console.error("Failed to save analytics:", err);
      }
    }

    toast.success("Saving analytics data...");

    if (isAdmin) {
      const finalBatchId =
        batchId ||
        currentBatchData?.batchId ||
        currentBatchData?.batch_id ||
        batchData?.batchId ||
        batchData?.batch_id ||
        (String(sessionId).startsWith("batch_") ? sessionId : null);

      const finalAssessmentId =
        currentBatchData?.assessmentId ||
        currentBatchData?.assessment_id ||
        batchData?.assessmentId ||
        batchData?.assessment_id ||
        null;

      const finalTeamId =
        currentBatchData?.teamId ||
        currentBatchData?.team_id ||
        batchData?.teamId ||
        batchData?.team_id ||
        teamId ||
        null;

      try {
        if (finalAssessmentId) {
          await axios.post(
            `${config.baseURL}/api/military/assessments/${finalAssessmentId}/end`,
            { decision: "completed" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else if (finalBatchId) {
          await axios.post(
            `${config.baseURL}/api/military/batch/${finalBatchId}/end`,
            { decision: "completed" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else {
          await axios.post(
            `${config.baseURL}/api/military/sessions/${dbSessionId}/complete`,
            { decision: "completed" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } catch (err) {
        console.error("Error completing military session:", err);
      }

      socketRef.current?.emit("admin-ended-military-session", {
        sessionId,
        batchId: finalBatchId,
        assessmentId: finalAssessmentId,
        endedBy: userId,
        timestamp: new Date().toISOString()
      });

      setReviewBatchId(finalBatchId);
      setReviewSessionId(!finalBatchId ? dbSessionId : null);
      setReviewTeamId(finalTeamId);

      setShowMilitaryReview(true);
      setConnected(false);

      return;
    }

    socketRef.current?.emit("soldier-left-military-session", {
      sessionId,
      soldierId,
      userId,
      batchId: batchId || null
    });

    await cleanupMedia();

    if (onClose) {
      setTimeout(() => onClose(), 100);
    }
  } catch (error) {
    console.error("Error in handleEndMeeting:", error);
  } finally {
    isEndingRef.current = false;
    setIsEnding(false);
  }
}, [
  isAdmin,
  batchId,
  currentBatchData,
  batchData,
  sessionId,
  dbSessionId,
  teamId,
  token,
  userId,
  soldierId,
  saveAnalytics,
  cleanupMedia,
  onClose
]);
  // ==================== SOCKET (UPDATED with debug) ====================
  useEffect(() => {
    if (!sessionId || !userId || hasEnded) return;
    // ✅ FIXED: Better soldierId extraction with debugging
    let effectiveSoldierId = soldierId;

    if (!effectiveSoldierId && currentBatchData) {
      // Try to find from candidates array
      const matchingCandidate = currentBatchData.candidates?.find(
        c => String(c.userId) === String(userId) || String(c.user_id) === String(userId)
      );

      if (matchingCandidate) {
        effectiveSoldierId = matchingCandidate.soldierId || matchingCandidate.soldier_id;
        console.log('🔍 Found soldierId from candidates:', effectiveSoldierId);
      }

      // If still not found, try soldierIds array
      if (!effectiveSoldierId && currentBatchData.soldierIds?.length > 0) {
        // For single candidate, use the first one
        if (currentBatchData.soldierIds.length === 1) {
          effectiveSoldierId = currentBatchData.soldierIds[0];
        } else {
          // For multiple candidates, try to match by index
          const candidateIndex = currentBatchData.candidates?.findIndex(
            c => String(c.userId) === String(userId) || String(c.user_id) === String(userId)
          );
          if (candidateIndex >= 0 && candidateIndex < currentBatchData.soldierIds.length) {
            effectiveSoldierId = currentBatchData.soldierIds[candidateIndex];
          }
        }
        console.log('🔍 Found soldierId from soldierIds array:', effectiveSoldierId);
      }
    }

    // ✅ Also check groupCandidates prop
    if (!effectiveSoldierId && groupCandidates?.length > 0) {
      const matchingGroupCandidate = groupCandidates.find(
        c => String(c.userId) === String(userId) || String(c.user_id) === String(userId)
      );
      if (matchingGroupCandidate) {
        effectiveSoldierId = matchingGroupCandidate.soldierId || matchingGroupCandidate.soldier_id;
        console.log('🔍 Found soldierId from groupCandidates:', effectiveSoldierId);
      }
    }

    console.log('🔍 Final effective soldierId:', effectiveSoldierId, 'userId:', userId);

    const socket = io(config.baseURL, {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      console.log('🔌 Socket connected, joining room:', sessionId);

      socket.emit("join-military-room", {
        sessionId,
        userType: isAdmin ? "admin" : "soldier",
        soldierId: effectiveSoldierId,
        userId: userId,
      });

      if (isAdmin) {
        console.log('👑 Admin requesting progress snapshot');
        socket.emit("military-get-all-progress", { sessionId });
      }
    });

    socket.on("connect_error", (error) => {
      console.error('❌ Socket connection error:', error);
    });

    socket.on("military-participants-update", (list) => {
      console.log('👥 Participants update received:', list.length, 'participants');
      setRemoteParticipants(list.filter(p => String(p.userId) !== String(userId)));
    });

    // FIX: Debug progress updates
    socket.on("military-progress-update", (payload) => {
      console.log('📊 Progress update received:', payload);
      const { soldierId: progId, answeredCount: ans, totalQuestions: tot, completed } = payload;
      const key = String(progId);

      console.log(`📊 Updating progress for soldier ${key}: ${ans}/${tot} (completed: ${completed})`);

      setSoldierProgress(prev => {
        const updated = {
          ...prev,
          [key]: {
            ...(prev[key] || {}),
            answeredCount: ans,
            totalQuestions: tot,
            completed: !!completed,
          },
        };
        console.log('📊 Updated soldierProgress:', updated);
        return updated;
      });

      // If this is our own progress echoed back, sync local state
      if (!isAdmin && String(progId) === String(soldierId)) {
        console.log('📊 Syncing local progress state');
        setAnsweredCount(ans);
        if (completed) setSessionCompleted(true);
      }
    });

    // FIX: Debug question events
    socket.on("military-question-answered", (payload) => {
      console.log('✅ Question answered event:', payload);
    });

    socket.on("military-question-skipped", (payload) => {
      console.log('⏭️ Question skipped event:', payload);
    });

    socket.on("military-soldier-completed", (payload) => {
      console.log('🎉 Soldier completed event:', payload);
    });

    // FIX: Debug bulk progress
    socket.on("military-all-progress", (payload) => {
      console.log('📦 Received all progress:', payload);
      const { progress } = payload;
      const progressMap = {};
      progress.forEach(p => {
        const key = String(p.soldierId);
        progressMap[key] = {
          answeredCount: p.answeredCount || 0,
          totalQuestions: p.totalQuestions || 30,
          completed: !!p.completed,
        };
      });
      console.log('📦 Setting progress map:', progressMap);
      setSoldierProgress(prev => {
        const merged = { ...prev, ...progressMap };
        console.log('📦 Merged progress:', merged);
        return merged;
      });
    });

    // ✅ FIXED: re-add listener for admin ending the session
    socket.on("military-session-ended", async () => {
      if (hasEndedRef.current || isEndingRef.current) return;
      console.log("🚨 Military session ended by admin");
      toast.info("Session ended by admin");
      if (!isAdmin && !analyticsSavedRef.current) {
        await saveAnalytics();
      }
      await cleanupMedia();
      if (onClose) setTimeout(() => onClose(), 100);
    });

    socket.on("disconnect", () => {
      console.log('🔌 Socket disconnected');
      setConnected(false);
    });

    return () => {
      console.log('🧹 Cleaning up socket connections');
      if (socket.connected) {
        socket.emit("leave-military-room", {
          sessionId,
          soldierId: effectiveSoldierId, // FIXED: Use effectiveSoldierId
          userId: userId
        });
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [sessionId, userId, token, isAdmin, hasEnded, soldierId, currentBatchData, groupCandidates, user]);

  // ==================== LOAD SESSION ====================
  useEffect(() => {
    if (isBatchSession && groupCandidates && groupCandidates.length > 0) {
      setRemoteParticipants(groupCandidates.map(c => ({
        userId: c.userId,
        displayName: c.displayName,
        soldierId: c.soldierId,
      })));

      if (!isAdmin && soldierId) {
        const questions = batchData?.questions || currentBatchData?.questions || [];

        if (questions.length === 0) {
          console.error('No questions available in batchData');
          return;
        }

        setAllQuestions(questions);
        setTotalQuestions(questions.length);
        console.log('Loaded questions:', questions.length);

        const firstAnsweredCount = 0;
        setAnsweredCount(firstAnsweredCount);

        if (firstAnsweredCount < questions.length) {
          const firstQuestion = questions[firstAnsweredCount];
          console.log('Showing question 1:', firstQuestion.text);
          setCurrentQuestion(firstQuestion);
          setQuestionIndex(firstAnsweredCount + 1);
          setShowQuestionPanel(true);
          setWaitingForNext(false);
          setSessionLoaded(true);
        } else {
          setSessionCompleted(true);
          setSessionLoaded(true);
        }
      }
      return;
    }

    if (!dbSessionId || !token || sessionLoaded) return;

    (async () => {
      try {
        const { data } = await axios.get(
          `${config.baseURL}/api/military/sessions/${dbSessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data.success) {
          const sd = data.session;
          setAllQuestions(sd.questions || []);
          setTotalQuestions(sd.total_questions || 30);
          setAnsweredCount(sd.answered_count || 0);
          setSessionLoaded(true);

          if (!isAdmin && sd.questions?.length > 0) {
            const startIndex = sd.answered_count || 0;
            if (startIndex < sd.questions.length) {
              setCurrentQuestion(sd.questions[startIndex]);
              setQuestionIndex(startIndex + 1);
              setShowQuestionPanel(true);
            } else {
              setSessionCompleted(true);
            }
          }
        }
      } catch (err) {
        console.error('Error loading session:', err);
      }
    })();
  }, [dbSessionId, token, isAdmin, sessionLoaded, isBatchSession, groupCandidates, soldierId, weekNumber, batchData, currentBatchData, sessionId]);

  // ==================== LIVEKIT TOKEN ====================
  useEffect(() => {
    if (!token || !userId || !sessionId) return;
    (async () => {
      try {
        const participantName = user?.first_name || user?.name || user?.username || `User ${userId}`;
        const metadata = {
          userId, isAdmin, soldierId: soldierId || null, sessionId,
          weekNumber: weekNumber || null, isBatchSession, batchId: batchId || null,
        };
        const { data } = await axios.post(
          `${config.baseURL}/api/livekit/token`,
          { roomName: livekitRoomName, participantName, metadata },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!data.success) { toast.error(data.message || "Failed to join"); return; }
        setLivekitToken(data.token);
        setLivekitUrl(data.url);
      } catch (err) {
        console.error('LiveKit token error:', err);
        toast.error('Failed to join video room');
      }
    })();
  }, [token, userId, sessionId, livekitRoomName, isAdmin, soldierId, user, isBatchSession, batchId, weekNumber]);

  // ==================== START ANALYTICS ON MOUNT ====================
  useEffect(() => {
    if (!isAdmin) startAnalyticsStream();
    return () => {
      if (analyticsStreamRef.current) {
        analyticsStreamRef.current.getTracks().forEach(t => t.stop());
        analyticsStreamRef.current = null;
      }
      stopAnalytics();
    };
  }, [isAdmin, startAnalyticsStream, stopAnalytics]);

  useEffect(() => {
    if (isAdmin) return;
    collectionIntervalRef.current = setInterval(() => {
      addCameraDataPoint();
      addVoiceDataPoint();
    }, 60000);
    return () => { if (collectionIntervalRef.current) clearInterval(collectionIntervalRef.current); };
  }, [isAdmin, addCameraDataPoint, addVoiceDataPoint]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isAdmin && !analyticsSavedRef.current) {
        addCameraDataPoint();
        addVoiceDataPoint();

        const payload = {
          soldierId,
          sessionId: dbSessionId,
          cameraMetrics: camHist.current,
          voiceMetrics: voiceHist.current,
          summary: {
            avgEyeContact: calcAvg(camHist.current.eye_contact_duration),
            avgBlinkRate: calcAvg(camHist.current.blink_rate),
            avgFacialTension: calcAvg(camHist.current.facial_tension),
            avgSmileAuthenticity: calcAvg(camHist.current.smile_authenticity),
            avgHeadTiltOpenness: calcAvg(camHist.current.head_tilt_openness),
            avgPitchVariability: calcAvg(voiceHist.current.pitch_variability),
            avgSpeechRate: calcAvg(voiceHist.current.speech_rate),
            avgPauses: calcAvg(voiceHist.current.pauses),
            avgVocalTremor: calcAvg(voiceHist.current.vocal_tremor),
            avgWarmthResonance: calcAvg(voiceHist.current.warmth_resonance),
            totalInterruptions: voiceHist.current.interrupt_patterns.filter(i => i.value === 1).length,
          },
        };

        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(
          `${config.baseURL}/api/military/sessions/${dbSessionId}/analytics/save`,
          blob
        );
        analyticsSavedRef.current = true;
      }
      analyticsStreamRef.current?.getTracks().forEach(t => t.stop());
      if (socketRef.current?.connected) {
        socketRef.current.emit('leave-military-room', { sessionId, soldierId });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAdmin, soldierId, dbSessionId, sessionId, calcAvg, addCameraDataPoint, addVoiceDataPoint]);

  // ==================== QUESTION HANDLING (UPDATED with debug) ====================
  const handleSubmitAnswer = useCallback(async () => {
    if (!currentQuestion || isSubmitting || isAdmin) return;
    setIsSubmitting(true);
    // FIXED: Determine correct soldierId
    const effectiveSoldierId = soldierId ||
      (currentBatchData?.candidates?.find(c => String(c.userId) === String(userId))?.soldierId) ||
      null;
    try {
      console.log('📤 Submitting answer with soldierId:', effectiveSoldierId);
      // console.log('📤 Submitting answer for question:', currentQuestion.id);

      const { data } = await axios.post(
        `${config.baseURL}/api/military/sessions/${dbSessionId}/submit-response`,
        { question_id: currentQuestion.id, answer_text: "[VERBAL RESPONSE]" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        const newCount = answeredCount + 1;
        setAnsweredCount(newCount);
        setShowQuestionPanel(false);
        setCurrentQuestion(null);

        // FIX: Emit to the correct room with all necessary data
        const progressPayload = {
          sessionId,
          soldierId: effectiveSoldierId, // FIXED: Use effectiveSoldierId
          userId: userId,
          questionId: currentQuestion.id,
          answeredCount: newCount,
          totalQuestions,
          completed: newCount >= totalQuestions,
        };

        console.log('📤 Emitting progress update:', progressPayload);

        socketRef.current?.emit("military-question-answered", progressPayload);
        socketRef.current?.emit("military-progress-update", progressPayload);

        if (newCount >= totalQuestions) {
          setSessionCompleted(true);
          console.log('🎉 All questions completed, emitting completion');
          socketRef.current?.emit("military-session-completed", progressPayload);
          if (!analyticsSavedRef.current) await saveAnalytics();
        } else {
          setWaitingForNext(true);
          setNextQuestionTime(Date.now() + 120000);

          if (questionTimerRef.current) clearTimeout(questionTimerRef.current);
          questionTimerRef.current = setTimeout(() => {
            setWaitingForNext(false);
            const nextIndex = newCount;
            if (nextIndex < allQuestions.length) {
              setCurrentQuestion(allQuestions[nextIndex]);
              setQuestionIndex(nextIndex + 1);
              setShowQuestionPanel(true);
            }
          }, 120000);
        }
      }
    } catch (err) {
      console.error('❌ Error submitting:', err);
      toast.error('Failed to submit answer');
    } finally {
      setIsSubmitting(false);
    }
  }, [currentQuestion, isSubmitting, isAdmin, dbSessionId, token, answeredCount, totalQuestions, allQuestions, soldierId, sessionId, saveAnalytics]);

  const handleSkipQuestion = useCallback(async () => {
    if (!currentQuestion || isSubmitting || isAdmin) return;
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(
        `${config.baseURL}/api/military/sessions/${dbSessionId}/submit-response`,
        { question_id: currentQuestion.id, answer_text: "[SKIPPED]" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        const newCount = answeredCount + 1;
        setAnsweredCount(newCount);
        setShowQuestionPanel(false);
        setCurrentQuestion(null);

        // FIX: always use sessionId for socket room consistency
        socketRef.current?.emit("military-question-skipped", {
          sessionId,
          soldierId,
          questionId: currentQuestion.id,
          answeredCount: newCount,
          totalQuestions,
        });

        socketRef.current?.emit("military-progress-update", {
          sessionId,
          soldierId,
          answeredCount: newCount,
          totalQuestions,
          completed: newCount >= totalQuestions,
        });

        if (newCount >= totalQuestions) {
          setSessionCompleted(true);
          socketRef.current?.emit("military-session-completed", {
            sessionId,
            soldierId,
            answeredCount: newCount,
            totalQuestions,
          });
          if (!analyticsSavedRef.current) await saveAnalytics();
        } else {
          setWaitingForNext(true);
          setNextQuestionTime(Date.now() + 120000);

          if (questionTimerRef.current) clearTimeout(questionTimerRef.current);
          questionTimerRef.current = setTimeout(() => {
            setWaitingForNext(false);
            const nextIndex = newCount;
            if (nextIndex < allQuestions.length) {
              setCurrentQuestion(allQuestions[nextIndex]);
              setQuestionIndex(nextIndex + 1);
              setShowQuestionPanel(true);
            }
          }, 120000);
        }
      }
    } catch (err) {
      console.error('Error skipping:', err);
      toast.error('Failed to skip question');
    } finally {
      setIsSubmitting(false);
    }
  }, [currentQuestion, isSubmitting, isAdmin, dbSessionId, token, answeredCount, totalQuestions, allQuestions, soldierId, sessionId, saveAnalytics]);

  useEffect(() => {
    if (!waitingForNext || !nextQuestionTime) return;

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((nextQuestionTime - Date.now()) / 1000));
      setTimeRemaining(remaining);
      if (remaining <= 0) setWaitingForNext(false);
    };

    tick();
    countdownRef.current = setInterval(tick, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [waitingForNext, nextQuestionTime]);

  useImperativeHandle(ref, () => ({
    endMeeting: handleEndMeeting,
    getAnalyticsData: () => isAdmin ? null : {
      cameraMetrics: camHist.current,
      voiceMetrics: voiceHist.current,
    },
  }));

  // ==================== RENDER ====================
if (hasEnded && !showMilitaryReview && !isAdmin) return null;

  if (isMinimized) {
    return (
      <div className="mil-minimized">
        <div className="mil-min-header">
          <span>Week {weekNumber || 1} Assessment</span>
          <button onClick={onToggleMinimize}><FaWindowMaximize /></button>
        </div>
        <div className="mil-min-video">
          <video ref={analyticsVideoRef} autoPlay muted playsInline />
          <div className="mil-min-label">You</div>
        </div>
      </div>
    );
  }

  if (!livekitToken || !livekitUrl) {
    return (
      <div className="mil-loading">
        <div className="mil-spinner"></div>
        <h3>Joining assessment room...</h3>
      </div>
    );
  }

  return (
    <div className="mil-container">
      <video
        ref={analyticsVideoRef}
        autoPlay
        muted
        playsInline
        style={{ display: "none" }}
      />

      <LiveKitRoom
        serverUrl={livekitUrl}
        token={livekitToken}
        connect={true}
        video={true}
        audio={true}
        ref={livekitRoomRef}
        onDisconnected={() => {
          if (!hasEndedRef.current) {
            if (!isAdmin && !analyticsSavedRef.current) {
              saveAnalytics().then(() => handleEndMeeting()).catch(() => handleEndMeeting());
            } else {
              handleEndMeeting();
            }
          }
        }}
      >
        <MilitaryVideoLayout onMaximize={handleMaximize} maximizedTrack={maximizedTrack} />
        <RoomAudioRenderer />
        <MilitaryControls
          isAdmin={isAdmin}
          onToggleMinimize={onToggleMinimize}
          handleEndMeeting={handleEndMeeting}
          isEnding={isEnding}
          setShowAdminPanel={setShowAdminPanel}
          showAdminPanel={showAdminPanel}
        />
      </LiveKitRoom>

      <MilitaryAdminProgress
        show={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
        remoteParticipants={remoteParticipants}
        soldierProgress={soldierProgress}
        totalQuestions={totalQuestions}
      />

      {!isAdmin && !sessionCompleted && (
        <>
          {showQuestionPanel && currentQuestion && (
            <MilitaryQuestionPanel
              show={showQuestionPanel}
              question={currentQuestion}
              index={questionIndex}
              total={totalQuestions}
              onSubmit={handleSubmitAnswer}
              onSkip={handleSkipQuestion}
              isSubmitting={isSubmitting}
            />
          )}
          {waitingForNext && !showQuestionPanel && (
            <MilitaryWaitingPanel
              timeRemaining={timeRemaining}
              answeredCount={answeredCount}
              totalQuestions={totalQuestions}
            />
          )}
        </>
      )}

      {!isAdmin && sessionCompleted && (
        <MilitaryCompletionPanel onClose={handleEndMeeting} />
      )}

      <MilitaryEndReviewModal
        show={showMilitaryReview}
        onClose={() => {
          setShowMilitaryReview(false);

          cleanupMedia();

          setTimeout(() => {
            if (onClose) onClose();
          }, 200);
        }}
        batchId={reviewBatchId}
        sessionId={reviewSessionId}
        teamId={reviewTeamId}
        token={token}
        isSingleSession={!isBatchSession}
        onCandidateAction={(soldierId, action) => {
          console.log("Soldier action:", soldierId, action);
        }}
      />
    </div>
  );
});

export default MilitarySessionVideoCall;